/**
 * Listing Pattern - Reusable UI Components
 * 
 * Kompakte Filter-Komponenten für Listen.
 */

import { useState, useRef, useEffect } from 'react';
import { Search, X, ChevronDown, RotateCcw, Check } from 'lucide-react';
import type { StatusFilter, SortDirection, TriStateFilter } from './types';

// ─────────────────────────────────────────────────────────────
// SearchInput
// ─────────────────────────────────────────────────────────────

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  onChangeImmediate?: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export function SearchInput({ 
  value, 
  onChange, 
  onChangeImmediate,
  placeholder = 'Suchen...', 
  className = '' 
}: SearchInputProps) {
  const [localValue, setLocalValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync with external value
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = (newValue: string) => {
    setLocalValue(newValue);
    onChange(newValue);
  };

  const handleClear = () => {
    setLocalValue('');
    if (onChangeImmediate) {
      onChangeImmediate('');
    } else {
      onChange('');
    }
    inputRef.current?.focus();
  };

  return (
    <div className={`relative ${className}`}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
      <input
        ref={inputRef}
        type="text"
        value={localValue}
        onChange={(e) => handleChange(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-9 pr-8 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
      />
      {localValue && (
        <button
          onClick={handleClear}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// StatusSelect
// ─────────────────────────────────────────────────────────────

interface StatusSelectProps {
  value: StatusFilter;
  onChange: (value: StatusFilter) => void;
  className?: string;
}

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'Alle' },
  { value: 'active', label: 'Aktiv' },
  { value: 'inactive', label: 'Inaktiv' },
];

export function StatusSelect({ value, onChange, className = '' }: StatusSelectProps) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as StatusFilter)}
      className={`px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white min-w-[90px] ${className}`}
    >
      {STATUS_OPTIONS.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  );
}

// ─────────────────────────────────────────────────────────────
// TriStateSelect
// ─────────────────────────────────────────────────────────────

interface TriStateSelectProps {
  value: TriStateFilter;
  onChange: (value: TriStateFilter) => void;
  label: string;
  presentLabel?: string;
  missingLabel?: string;
  className?: string;
}

export function TriStateSelect({ 
  value, 
  onChange, 
  label,
  presentLabel = 'Vorhanden',
  missingLabel = 'Fehlt',
  className = '' 
}: TriStateSelectProps) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as TriStateFilter)}
      className={`px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white min-w-[130px] ${className}`}
      title={label}
    >
      <option value="all">{label}: Alle</option>
      <option value="present">{presentLabel}</option>
      <option value="missing">{missingLabel}</option>
    </select>
  );
}

// ─────────────────────────────────────────────────────────────
// MultiSelectDropdown
// ─────────────────────────────────────────────────────────────

interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectDropdownProps {
  value: string[];
  onChange: (value: string[]) => void;
  onToggle: (value: string) => void;
  options: MultiSelectOption[];
  label: string;
  className?: string;
}

export function MultiSelectDropdown({
  value,
  onChange,
  onToggle,
  options,
  label,
  className = '',
}: MultiSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedCount = value.length;
  const displayLabel = selectedCount === 0 
    ? label 
    : selectedCount === 1 
      ? options.find(o => o.value === value[0])?.label || value[0]
      : `${selectedCount} ausgewählt`;

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm bg-white hover:bg-slate-50 transition-colors min-w-[140px] ${
          selectedCount > 0 ? 'border-blue-300 text-blue-700' : 'border-slate-300 text-slate-700'
        }`}
      >
        <span className="flex-1 text-left truncate">{displayLabel}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full min-w-[180px] bg-white border border-slate-200 rounded-lg shadow-lg py-1">
          {options.map(opt => {
            const isSelected = value.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onToggle(opt.value)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-left"
              >
                <div className={`w-4 h-4 border rounded flex items-center justify-center ${
                  isSelected ? 'bg-blue-600 border-blue-600' : 'border-slate-300'
                }`}>
                  {isSelected && <Check className="w-3 h-3 text-white" />}
                </div>
                <span>{opt.label}</span>
              </button>
            );
          })}
          {selectedCount > 0 && (
            <>
              <div className="border-t border-slate-100 my-1" />
              <button
                type="button"
                onClick={() => onChange([])}
                className="w-full px-3 py-2 text-sm text-slate-500 hover:bg-slate-50 text-left"
              >
                Auswahl aufheben
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ResetButton
// ─────────────────────────────────────────────────────────────

interface ResetButtonProps {
  onClick: () => void;
  hasActiveFilters: boolean;
  className?: string;
}

export function ResetButton({ onClick, hasActiveFilters, className = '' }: ResetButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!hasActiveFilters}
      className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg transition-colors ${
        hasActiveFilters 
          ? 'text-slate-600 hover:text-slate-900 hover:bg-slate-100' 
          : 'invisible'
      } ${className}`}
      title="Filter zurücksetzen"
    >
      <RotateCcw className="w-4 h-4" />
      <span className="hidden sm:inline">Zurücksetzen</span>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// SortableHeader
// ─────────────────────────────────────────────────────────────

interface SortableHeaderProps {
  children: React.ReactNode;
  sortKey: string;
  currentSortKey: string;
  currentSortDir: SortDirection;
  onSort: (key: string) => void;
  className?: string;
}

export function SortableHeader({
  children,
  sortKey,
  currentSortKey,
  currentSortDir,
  onSort,
  className = '',
}: SortableHeaderProps) {
  const isActive = currentSortKey === sortKey;
  
  return (
    <th
      className={`px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer hover:bg-slate-100 select-none ${className}`}
      onClick={() => onSort(sortKey)}
    >
      <div className="flex items-center gap-1">
        {children}
        {isActive && (
          <span className="text-blue-600">
            {currentSortDir === 'asc' ? '↑' : '↓'}
          </span>
        )}
      </div>
    </th>
  );
}

// ─────────────────────────────────────────────────────────────
// FilterBar (Wrapper für kompaktes Layout)
// ─────────────────────────────────────────────────────────────

interface FilterBarProps {
  children: React.ReactNode;
  className?: string;
}

export function FilterBar({ children, className = '' }: FilterBarProps) {
  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      {children}
    </div>
  );
}
